import type { ReactNode } from 'react';
import { Checkbox, Input, Label } from '@artstorefronts/ui';
import { Link } from 'react-router-dom';

export type Field = {
  key: string;
  group: string;
  label: string;
  description: string;
  default: number;
  min: number;
  max: number;
  isFloat: boolean;
  isToggle: boolean;
};

export const GROUPS: { key: string; title: string; body: ReactNode }[] = [
  { key: 'ranking', title: 'Ranking', body: 'How the trending score is computed and which posts are eligible.' },
  {
    key: 'promoted',
    title: 'Promoted posts',
    body: 'Where admin-promoted posts land in trending. High Priority always sits at the top; these knobs place Normal Priority posts.',
  },
  {
    key: 'newMembers',
    title: 'New-member density',
    body: (
      <>
        A <span className="font-medium">position</span> is a slot in the ranked feed (1st post shown, 2nd, 3rd…). A{' '}
        <span className="font-medium">window</span> is a run of consecutive positions that slides down the whole feed.
        These knobs keep onboarding “welcome” posts from clumping: at most{' '}
        <span className="font-medium">posts per window</span> of them in any{' '}
        <span className="font-medium">window size</span> positions in a row — personalized posts included.
      </>
    ),
  },
  {
    key: 'personalization',
    title: 'Personalization',
    body: 'What logged-in viewers get mixed into trending on top of the global list. Logged-out viewers always see the global list.',
  },
  {
    key: 'feedCards',
    title: 'Feed cards',
    body: (
      <>
        How often platform cards appear between posts. The cards themselves are managed in{' '}
        <Link to="/admin/feed-cards" className="font-medium text-gray-900 underline">
          Feed Cards
        </Link>
        .
      </>
    ),
  },
];

function FieldInput({ field, value, onChange }: { field: Field; value: string; onChange: (value: string) => void }) {
  if (field.isToggle) {
    return (
      <div className="space-y-1">
        <Label htmlFor={`fs-${field.key}`} className="flex items-center gap-2">
          <Checkbox
            id={`fs-${field.key}`}
            checked={value === '1'}
            onCheckedChange={(checked) => onChange(checked === true ? '1' : '0')}
          />
          {field.label}
        </Label>
        <p className="text-xs text-gray-500">{field.description}</p>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={`fs-${field.key}`}>
        {field.label}{' '}
        <span className="text-gray-400">
          ({field.min}–{field.max})
        </span>
      </Label>
      <Input
        id={`fs-${field.key}`}
        type="number"
        step={field.isFloat ? '0.1' : '1'}
        min={field.min}
        max={field.max}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="text-xs text-gray-500">{field.description}</p>
    </div>
  );
}

export function FieldGroup({
  group,
  fields,
  values,
  onChange,
}: {
  group: (typeof GROUPS)[number];
  fields: Field[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
}) {
  if (fields.length === 0) return null;

  return (
    <section className="space-y-4 border-t border-gray-200 pt-5">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{group.title}</h3>
        <p className="mt-1 text-xs text-gray-600">{group.body}</p>
      </div>
      {fields.map((field) => (
        <FieldInput
          key={field.key}
          field={field}
          value={values[field.key] ?? ''}
          onChange={(value) => onChange(field.key, value)}
        />
      ))}
    </section>
  );
}
